// src/hooks/usePolledResource.js
//
// Generic polling data hook. Fetches via the provided function, exposes
// data / loading / error. Optionally re-fetches on an interval.
//
// The caller must include every value the fetcher closes over in `deps`.

import { useState, useEffect } from 'react';

export function usePolledResource(fetcher, deps, options = {}) {
  const {
    enabled = true,
    refetchInterval = null,
    initialData = null,
    onSuccess,
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetcher();
        if (cancelled) return;
        setData(result);
        if (onSuccess) onSuccess(result);
      } catch (err) {
        if (cancelled) return;
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    if (!refetchInterval) {
      return () => { cancelled = true; };
    }

    const id = setInterval(run, refetchInterval);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}